import {
  type SearchResult,
  isCloneType,
  isDemoType,
  isOverlayType,
  isPlaybookType,
  isTourType,
} from '@carboncopyco/data';
import {
  DemoIndicatorColoredIcon,
  PlaybookIcon,
  RecordingIndicatorColoredIcon,
  TourIndicatorColoredIcon,
} from '@carboncopyco/icons';
import { RecordingTypes } from '@carboncopyco/types';
import {
  LoadingDots,
  SkeletonRowLoading,
  TextBase,
  TextButton,
  TooltipBubble,
  UserBasicInfo,
} from '@carboncopyco/ui';
import {
  MMMDoYYYY,
  dateUTCFormat,
  integerWithCommas,
} from '@carboncopyco/utils';
import type { SerializedError } from '@reduxjs/toolkit';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query';
import { type ReactElement, useEffect, useRef } from 'react';
import { useInView } from 'react-intersection-observer';
import { type Col, FixedTable } from '../../../FixedTable';
import type { ExcludedEntities } from '../../../types';
import { WorkspacePageTableRowWrapper } from '../WorkspacePageTableRowWrapper';
import {
  type ExtendedWorkspaceFolder,
  isFolderType,
} from '../WorkspacePageTableView';

interface FolderPageTableViewProps {
  items: SearchResult[];
  isLoading: boolean;
  isLoadingBatch: boolean;
  error: FetchBaseQueryError | SerializedError | undefined;
  fetchNextBatch: () => void;
}

const showIconByEntity: Record<ExcludedEntities, ReactElement> = {
  record: <RecordingIndicatorColoredIcon size={18} />,
  demo: <DemoIndicatorColoredIcon size={18} />,
  tour: <TourIndicatorColoredIcon size={18} />,
  overlay: <DemoIndicatorColoredIcon size={18} />,
  playbook: <PlaybookIcon size={18} />,
};

const showTooltipByEntity: Record<ExcludedEntities, string> = {
  record: 'Clone',
  demo: 'Demo (cloned app)',
  tour: 'Tour',
  overlay: 'Demo (live app)',
  playbook: 'Playbooks contain a variety of assets and web resources',
};

const getAssetNameByEntity = (item: SearchResult | ExtendedWorkspaceFolder) => {
  if (isCloneType(item)) {
    return item.recordName;
  }
  if (isDemoType(item) || isOverlayType(item)) {
    return item.demoName;
  }
  if (isTourType(item)) {
    return item.tourName;
  }
  if (isPlaybookType(item) || isFolderType(item)) {
    return item.name;
  }
  return 'Missing title';
};

const cols: Col<SearchResult>[] = [
  {
    title: 'Type',
    width: 45,
    render: (item: SearchResult) => {
      return (
        <TooltipBubble
          title={showTooltipByEntity[item.entityType]}
          placement="top"
        >
          <div className="flex flex-col">
            {showIconByEntity[item.entityType]}
          </div>
        </TooltipBubble>
      );
    },
  },
  {
    title: 'Name',
    width: 290,
    maxContentWidth: 270,
    render: (item) => {
      return (
        <TooltipBubble
          title={getAssetNameByEntity(item)}
          placement="top-start"
        >
          <div className="truncate">
            <TextButton
              ellipsis
              label={getAssetNameByEntity(item)}
              color="text-ds-natural-700"
            />
          </div>
        </TooltipBubble>
      );
    },
  },
  {
    title: 'Created by',
    width: 200,
    render: (item) => (
      <UserBasicInfo
        user={item.creator}
        truncateWidth={140}
        textColor="text-ds-natural-600"
        tooltip={item?.creator?.name?.length > 13}
        size="small"
      />
    ),
  },
  {
    title: 'Last edited',
    width: 168,
    maxContentWidth: 340,
    render: (item) => (
      <TooltipBubble
        title={`Edited on ${dateUTCFormat(item.updateTime)}`}
        placement="top"
      >
        <TextBase
          color="text-ds-natural-300"
          label={MMMDoYYYY(item.updateTime)}
        />
      </TooltipBubble>
    ),
  },
  {
    title: 'Creation source',
    width: 120,
    render: (item) => {
      const isNativeRecording =
        (isCloneType(item) || isDemoType(item)) &&
        item.recordingType === RecordingTypes.native;
      const isTourFromDesktop = isTourType(item) && item.source === 'desktop';
      const label =
        isNativeRecording || isTourFromDesktop ? 'Desktop app' : 'Website';
      return (
        <TextBase
          label={label}
          color="text-ds-natural-600"
        />
      );
    },
  },
  {
    title: 'Presentations',
    width: 100,
    alignment: 'right',
    render: (item) => (
      <TextBase
        label={
          (isDemoType(item) || isOverlayType(item)) &&
          item.totalPresentations &&
          item.totalPresentations > 0
            ? integerWithCommas(item.totalPresentations)
            : ''
        }
        color="text-ds-natural-600"
      />
    ),
  },
  {
    title: 'Shares',
    width: 70,
    alignment: 'right',
    render: (item) => (
      <TextBase
        label={
          (isDemoType(item) || isTourType(item)) &&
          item.totalShares &&
          item.totalShares > 0
            ? integerWithCommas(item.totalShares)
            : ''
        }
        color="text-ds-natural-600"
      />
    ),
  },
];

export const FolderPageTableView = ({
  items,
  isLoading,
  isLoadingBatch,
  error,
  fetchNextBatch,
}: FolderPageTableViewProps) => {
  const tableParentRef = useRef<HTMLDivElement>();

  const [listBottomRef, _, entry] = useInView({
    root: tableParentRef.current,
  });

  useEffect(() => {
    if (items?.length && entry?.isIntersecting) {
      fetchNextBatch();
    }
  }, [entry, fetchNextBatch, items]);

  return (
    <div
      ref={tableParentRef}
      className="overflow-y-auto relative w-full"
    >
      <FixedTable<SearchResult>
        headerRowClassName="pt-4 pb-1 sticky top-0 left-0 right-0 bg-ds-accent3-100 z-[5]"
        rowWrapper={WorkspacePageTableRowWrapper}
        cols={cols}
        data={items}
        isLoading={isLoading}
        error={error}
        loader={<SkeletonRowLoading itemsCount={10} />}
        noWrapperDiv
      />
      {isLoadingBatch && (
        <div className="flex justify-center">
          <LoadingDots />
        </div>
      )}
      <div ref={listBottomRef} />
    </div>
  );
};
